import {
  LOOM_AIRSHOW_ARCHIVE_SHA256,
  LOOM_AIRSHOW_CATALOG_REVISION,
} from "./loomAirshowPresetTrust";
import {
  parseHeartbeatSemanticRejections,
} from "./airshowHeartbeatSemanticRejections";
import {
  HEARTBEAT_CATALOG_ID,
  HEARTBEAT_LANES,
  HEARTBEAT_TARGETS,
  HEARTBEAT_TERMINAL_STATES,
  type AirshowHeartbeatModelsPage,
  type AirshowHeartbeatReport,
  type AirshowHeartbeatRun,
  type HeartbeatFailureCluster,
  type HeartbeatLane,
  type HeartbeatLaneMetrics,
  type HeartbeatLaneResult,
  type HeartbeatModelResult,
  type HeartbeatRunState,
  type HeartbeatSelectedModel,
  type HeartbeatTarget,
  type HeartbeatTargetResult,
  type HeartbeatTargetSummary,
} from "./airshowHeartbeatTypes";

export type { HeartbeatSemanticRejection } from "./airshowHeartbeatSemanticRejections";
export * from "./airshowHeartbeatTypes";

type Row = Record<string, unknown>;
function row(value: unknown, path: string): Row {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error(`${path} must be an object`);
  return value as Row;
}
function exact(value: Row, keys: readonly string[], path: string): void {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  if (actual.length !== expected.length || actual.some((key, index) => key !== expected[index])) {
    throw new Error(`${path} has an unsupported field set`);
  }
}
function text(value: unknown, path: string): string {
  if (typeof value !== "string" || !value.trim()) throw new Error(`${path} must be non-empty text`);
  return value;
}
function code(value: unknown, path: string): string {
  const result = text(value, path);
  if (!/^[a-z0-9][a-z0-9-]{0,127}$/.test(result)) throw new Error(`${path} is not a bounded code`);
  return result;
}
function integer(value: unknown, path: string, maximum = 1_000_000): number {
  if (!Number.isSafeInteger(value) || (value as number) < 0 || (value as number) > maximum) {
    throw new Error(`${path} must be a bounded nonnegative integer`);
  }
  return value as number;
}
function bool(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") throw new Error(`${path} must be boolean`);
  return value;
}
function timestamp(value: unknown, path: string): string {
  const result = text(value, path);
  if (!Number.isFinite(Date.parse(result))) throw new Error(`${path} must be an ISO timestamp`);
  return result;
}
function uuid(value: unknown, path: string): string {
  const result = text(value, path);
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(result)) {
    throw new Error(`${path} must be a UUID`);
  }
  return result;
}
function sha(value: unknown, path: string, prefixed = false): string {
  const result = text(value, path);
  const pattern = prefixed ? /^sha256:[a-f0-9]{64}$/ : /^[a-f0-9]{64}$/;
  if (!pattern.test(result)) throw new Error(`${path} must be a canonical SHA-256 identity`);
  return result;
}
function oneOf<T extends string>(value: unknown, choices: readonly T[], path: string): T {
  if (typeof value !== "string" || !choices.includes(value as T)) throw new Error(`${path} is unsupported`);
  return value as T;
}
function nullable<T>(value: unknown, parse: (child: unknown) => T): T | null {
  return value === null ? null : parse(value);
}
function sortedUnique<T extends string | number>(values: unknown, parse: (child: unknown, path: string) => T, path: string): readonly T[] {
  if (!Array.isArray(values)) throw new Error(`${path} must be an array`);
  const result = values.map((item, index) => parse(item, `${path}[${index}]`));
  if (result.some((item, index) => {
    if (index === 0) return false;
    const previous = result[index - 1]!;
    return typeof previous === "number" && typeof item === "number"
      ? previous >= item : String(previous) >= String(item);
  })) {
    throw new Error(`${path} must be sorted and unique`);
  }
  return result;
}
function entryId(value: unknown, path: string): string {
  const result = text(value, path);
  if (!/^airshow\/[a-z0-9._-]{1,128}\/[a-z0-9._-]{1,128}$/.test(result)) throw new Error(`${path} is invalid`);
  return result;
}
function selectedModel(value: unknown, path: string): HeartbeatSelectedModel {
  const source = row(value, path); exact(source, ["entry_id", "archive_member_path"], path);
  const id = entryId(source.entry_id, `${path}.entry_id`);
  const member = text(source.archive_member_path, `${path}.archive_member_path`);
  const match = /^models\/([^/]+)\/([^/]+)\.vsp3$/.exec(member);
  const [, folder, stem] = id.split("/");
  if (!match || match[1]!.toLowerCase() !== folder || match[2]!.toLowerCase() !== stem
      || member.includes("\\") || member.split("/").includes("..")) throw new Error(`${path} identity and member disagree`);
  return { entry_id: id, archive_member_path: member };
}
function canonicalTargets(value: unknown, path: string): readonly HeartbeatTarget[] {
  if (!Array.isArray(value) || value.length < 1 || value.length > 4) throw new Error(`${path} must be a target array`);
  const parsed = value.map((item, index) => oneOf(item, HEARTBEAT_TARGETS, `${path}[${index}]`));
  const expected = HEARTBEAT_TARGETS.filter((target) => parsed.includes(target));
  if (parsed.some((target, index) => target !== expected[index])) throw new Error(`${path} must be canonical and unique`);
  return parsed;
}

export function isHeartbeatTerminal(state: HeartbeatRunState): boolean {
  return (HEARTBEAT_TERMINAL_STATES as readonly string[]).includes(state);
}

export function isHeartbeatReportReady(run: AirshowHeartbeatRun): boolean {
  return isHeartbeatTerminal(run.state) && run.report_revision !== null;
}

export function parseAirshowHeartbeatRun(value: unknown, expected?: {
  projectId?: string; runId?: string; catalogRevision?: string; archiveSha256?: string;
}): AirshowHeartbeatRun {
  const source = row(value, "heartbeat.run");
  exact(source, ["schema", "run_id", "project_id", "catalog", "scope", "prior_run_id", "targets", "state",
    "created_at", "started_at", "updated_at", "last_pulse_at", "finished_at", "total_models",
    "processed_models", "succeeded_models", "failed_models", "event_sequence", "last_event_digest", "report_revision",
    "cancel_requested", "pulse_stale", "current_model", "error"], "heartbeat.run");
  if (source.schema !== "loom.airshow-heartbeat-run/v1") throw new Error("heartbeat.run schema is unsupported");
  const catalogSource = row(source.catalog, "heartbeat.run.catalog");
  exact(catalogSource, ["catalog_id", "catalog_revision", "archive_sha256"], "heartbeat.run.catalog");
  if (catalogSource.catalog_id !== HEARTBEAT_CATALOG_ID) throw new Error("heartbeat catalog identity is unsupported");
  const catalog = {
    catalog_id: HEARTBEAT_CATALOG_ID,
    catalog_revision: sha(catalogSource.catalog_revision, "heartbeat.run.catalog.catalog_revision", true),
    archive_sha256: sha(catalogSource.archive_sha256, "heartbeat.run.catalog.archive_sha256"),
  };
  const result: AirshowHeartbeatRun = {
    schema: "loom.airshow-heartbeat-run/v1",
    run_id: uuid(source.run_id, "heartbeat.run.run_id"),
    project_id: uuid(source.project_id, "heartbeat.run.project_id"),
    catalog,
    scope: oneOf(source.scope, ["all", "failures-from"] as const, "heartbeat.run.scope"),
    prior_run_id: nullable(source.prior_run_id, (child) => uuid(child, "heartbeat.run.prior_run_id")),
    targets: canonicalTargets(source.targets, "heartbeat.run.targets"),
    state: oneOf(source.state, ["queued", "running", "cancelling", ...HEARTBEAT_TERMINAL_STATES] as const, "heartbeat.run.state"),
    created_at: timestamp(source.created_at, "heartbeat.run.created_at"),
    started_at: nullable(source.started_at, (child) => timestamp(child, "heartbeat.run.started_at")),
    updated_at: timestamp(source.updated_at, "heartbeat.run.updated_at"),
    last_pulse_at: nullable(source.last_pulse_at, (child) => timestamp(child, "heartbeat.run.last_pulse_at")),
    finished_at: nullable(source.finished_at, (child) => timestamp(child, "heartbeat.run.finished_at")),
    total_models: integer(source.total_models, "heartbeat.run.total_models"),
    processed_models: integer(source.processed_models, "heartbeat.run.processed_models"),
    succeeded_models: integer(source.succeeded_models, "heartbeat.run.succeeded_models"),
    failed_models: integer(source.failed_models, "heartbeat.run.failed_models"),
    event_sequence: integer(source.event_sequence, "heartbeat.run.event_sequence", 100_000_000),
    last_event_digest: nullable(source.last_event_digest, (child) => sha(child, "heartbeat.run.last_event_digest", true)),
    report_revision: nullable(source.report_revision, (child) => sha(child, "heartbeat.run.report_revision", true)),
    cancel_requested: bool(source.cancel_requested, "heartbeat.run.cancel_requested"),
    pulse_stale: bool(source.pulse_stale, "heartbeat.run.pulse_stale"),
    current_model: nullable(source.current_model, (child) => {
      const current = row(child, "heartbeat.run.current_model");
      exact(current, ["ordinal", "entry_id"], "heartbeat.run.current_model");
      return { ordinal: integer(current.ordinal, "heartbeat.run.current_model.ordinal"), entry_id: entryId(current.entry_id, "heartbeat.run.current_model.entry_id") };
    }),
    error: nullable(source.error, (child) => {
      const error = row(child, "heartbeat.run.error"); exact(error, ["code", "message", "retryable"], "heartbeat.run.error");
      return { code: code(error.code, "heartbeat.run.error.code"), message: text(error.message, "heartbeat.run.error.message"), retryable: bool(error.retryable, "heartbeat.run.error.retryable") };
    }),
  };
  if (expected?.projectId && result.project_id !== expected.projectId) throw new Error("heartbeat project identity is stale");
  if (expected?.runId && result.run_id !== expected.runId) throw new Error("heartbeat run identity is stale");
  if (expected?.catalogRevision && catalog.catalog_revision !== expected.catalogRevision) throw new Error("heartbeat catalog revision is stale");
  if (expected?.archiveSha256 && catalog.archive_sha256 !== expected.archiveSha256) throw new Error("heartbeat archive identity is stale");
  if (catalog.catalog_revision !== LOOM_AIRSHOW_CATALOG_REVISION || catalog.archive_sha256 !== LOOM_AIRSHOW_ARCHIVE_SHA256) throw new Error("heartbeat corpus is not the pinned Airshow revision");
  if ((result.scope === "failures-from") !== (result.prior_run_id !== null)) throw new Error("heartbeat prior run and scope disagree");
  if (result.processed_models > result.total_models || result.succeeded_models + result.failed_models !== result.processed_models) throw new Error("heartbeat run counts disagree");
  const terminal = isHeartbeatTerminal(result.state);
  if (terminal !== (result.finished_at !== null) || terminal !== (result.report_revision !== null)
      || (terminal && result.current_model !== null) || (!terminal && result.started_at === null && result.state !== "queued")) {
    throw new Error("heartbeat terminal lifecycle or report revision is incoherent");
  }
  if ((result.event_sequence === 0) !== (result.last_event_digest === null)) throw new Error("heartbeat event chain is incoherent");
  if (Date.parse(result.updated_at) < Date.parse(result.created_at)) throw new Error("heartbeat timestamps are reversed");
  return result;
}

function parseMetrics(value: unknown, path: string): HeartbeatLaneMetrics {
  const source = row(value, path);
  const semanticRejectionsPresent = Object.prototype.hasOwnProperty.call(source, "semantic_rejections");
  const authoringPresent = Object.prototype.hasOwnProperty.call(source, "authoring_receipt_revision");
  const authoringKeys = ["authoring_receipt_revision", "aircraft_ir_revision", "aircraft_topology_revision",
    "hierarchy_receipt_revision", "source_occurrence_count", "represented_occurrence_count",
    "policy_excluded_occurrence_count", "control_occurrence_count", "represented_control_count",
    "rejected_control_count", "unresolved_control_count", "control_rejection_codes",
    "propulsor_occurrence_count", "represented_propulsor_count", "rejected_propulsor_count",
    "unresolved_propulsor_count", "propulsor_rejection_codes", "body_representation_kinds"];
  exact(source, ["component_count", "supported_component_count", "skipped_component_count", "unknown_component_types",
    "source_component_count", "realized_component_count", "source_surface_count", "realized_surface_count",
    "skipped_surface_count", "unknown_xsec_types", "rejected_feature_ids", "unsupported_declarations",
    ...(semanticRejectionsPresent ? ["semantic_rejections"] : []),
    ...(authoringPresent ? authoringKeys : []),
    "representation", "fidelity", "geometry_complete", "export_order_attested", "artifact_sha256"], path);
  const semanticRejections = semanticRejectionsPresent
    ? parseHeartbeatSemanticRejections(source.semantic_rejections, `${path}.semantic_rejections`) : [];
  const result: HeartbeatLaneMetrics = {
    component_count: nullable(source.component_count, (child) => integer(child, `${path}.component_count`)),
    supported_component_count: nullable(source.supported_component_count, (child) => integer(child, `${path}.supported_component_count`)),
    skipped_component_count: nullable(source.skipped_component_count, (child) => integer(child, `${path}.skipped_component_count`)),
    source_component_count: nullable(source.source_component_count, (child) => integer(child, `${path}.source_component_count`)),
    realized_component_count: nullable(source.realized_component_count, (child) => integer(child, `${path}.realized_component_count`)),
    source_surface_count: nullable(source.source_surface_count, (child) => integer(child, `${path}.source_surface_count`)),
    realized_surface_count: nullable(source.realized_surface_count, (child) => integer(child, `${path}.realized_surface_count`)),
    skipped_surface_count: nullable(source.skipped_surface_count, (child) => integer(child, `${path}.skipped_surface_count`)),
    unknown_component_types: sortedUnique(source.unknown_component_types, text, `${path}.unknown_component_types`),
    unknown_xsec_types: sortedUnique(source.unknown_xsec_types, integer, `${path}.unknown_xsec_types`),
    rejected_feature_ids: sortedUnique(source.rejected_feature_ids, text, `${path}.rejected_feature_ids`),
    unsupported_declarations: sortedUnique(source.unsupported_declarations, text, `${path}.unsupported_declarations`),
    semantic_rejections: semanticRejections,
    representation: nullable(source.representation, (child) => text(child, `${path}.representation`)),
    fidelity: nullable(source.fidelity, (child) => text(child, `${path}.fidelity`)),
    geometry_complete: nullable(source.geometry_complete, (child) => bool(child, `${path}.geometry_complete`)),
    export_order_attested: nullable(source.export_order_attested, (child) => bool(child, `${path}.export_order_attested`)),
    artifact_sha256: nullable(source.artifact_sha256, (child) => sha(child, `${path}.artifact_sha256`)),
    ...(authoringPresent ? {
      authoring_receipt_revision: sha(source.authoring_receipt_revision, `${path}.authoring_receipt_revision`, true),
      aircraft_ir_revision: nullable(source.aircraft_ir_revision, (child) => sha(child, `${path}.aircraft_ir_revision`, true)),
      aircraft_topology_revision: nullable(source.aircraft_topology_revision, (child) => sha(child, `${path}.aircraft_topology_revision`, true)),
      hierarchy_receipt_revision: nullable(source.hierarchy_receipt_revision, (child) => sha(child, `${path}.hierarchy_receipt_revision`, true)),
      source_occurrence_count: integer(source.source_occurrence_count, `${path}.source_occurrence_count`),
      represented_occurrence_count: integer(source.represented_occurrence_count, `${path}.represented_occurrence_count`),
      policy_excluded_occurrence_count: integer(source.policy_excluded_occurrence_count, `${path}.policy_excluded_occurrence_count`),
      control_occurrence_count: integer(source.control_occurrence_count, `${path}.control_occurrence_count`),
      represented_control_count: integer(source.represented_control_count, `${path}.represented_control_count`),
      rejected_control_count: integer(source.rejected_control_count, `${path}.rejected_control_count`),
      unresolved_control_count: integer(source.unresolved_control_count, `${path}.unresolved_control_count`),
      control_rejection_codes: sortedUnique(source.control_rejection_codes, code, `${path}.control_rejection_codes`),
      propulsor_occurrence_count: integer(source.propulsor_occurrence_count, `${path}.propulsor_occurrence_count`),
      represented_propulsor_count: integer(source.represented_propulsor_count, `${path}.represented_propulsor_count`),
      rejected_propulsor_count: integer(source.rejected_propulsor_count, `${path}.rejected_propulsor_count`),
      unresolved_propulsor_count: integer(source.unresolved_propulsor_count, `${path}.unresolved_propulsor_count`),
      propulsor_rejection_codes: sortedUnique(source.propulsor_rejection_codes, code, `${path}.propulsor_rejection_codes`),
      body_representation_kinds: sortedUnique(
        source.body_representation_kinds,
        (child, childPath) => oneOf(child, ["adapted", "exact-attested", "native-triangle"] as const, childPath),
        `${path}.body_representation_kinds`,
      ),
    } : {}),
  };
  if (result.component_count !== null && ((result.supported_component_count ?? 0) > result.component_count
      || (result.skipped_component_count ?? 0) > result.component_count)) throw new Error(`${path} component counts disagree`);
  if (result.source_component_count !== null && (result.realized_component_count ?? 0) > result.source_component_count) throw new Error(`${path} realized component count exceeds source`);
  if (result.source_surface_count !== null && (result.realized_surface_count ?? 0) > result.source_surface_count) throw new Error(`${path} realized surface count exceeds source`);
  if (authoringPresent && ((result.source_occurrence_count ?? 0) === 0
      || result.component_count !== result.source_occurrence_count
      || result.supported_component_count !== result.represented_occurrence_count
      || result.skipped_component_count !== result.source_occurrence_count!
        - result.represented_occurrence_count! - result.policy_excluded_occurrence_count!
      || (result.policy_excluded_occurrence_count ?? 0) !== 0
      || (result.represented_occurrence_count ?? 0) + (result.policy_excluded_occurrence_count ?? 0) > (result.source_occurrence_count ?? 0)
      || (result.represented_control_count ?? 0) > (result.control_occurrence_count ?? 0)
      || (result.represented_control_count ?? 0) + (result.rejected_control_count ?? 0)
        + (result.unresolved_control_count ?? 0) !== (result.control_occurrence_count ?? 0)
      || ((result.rejected_control_count ?? 0) === 0)
        !== ((result.control_rejection_codes?.length ?? 0) === 0)
      || (result.represented_propulsor_count ?? 0) > (result.propulsor_occurrence_count ?? 0)
      || (result.represented_propulsor_count ?? 0) + (result.rejected_propulsor_count ?? 0)
        + (result.unresolved_propulsor_count ?? 0) !== (result.propulsor_occurrence_count ?? 0)
      || ((result.rejected_propulsor_count ?? 0) === 0)
        !== ((result.propulsor_rejection_codes?.length ?? 0) === 0))) {
    throw new Error(`${path} authoring occurrence counts disagree`);
  }
  return result;
}

function completeSemantic(metrics: HeartbeatLaneMetrics): boolean {
  if (metrics.authoring_receipt_revision !== undefined) {
    return metrics.source_occurrence_count !== undefined
      && metrics.represented_occurrence_count !== undefined
      && metrics.policy_excluded_occurrence_count !== undefined
      && metrics.component_count === metrics.source_occurrence_count
      && metrics.policy_excluded_occurrence_count === 0
      && metrics.represented_occurrence_count === metrics.source_occurrence_count
      && metrics.supported_component_count === metrics.represented_occurrence_count
      && metrics.skipped_component_count === 0
      && metrics.control_occurrence_count === metrics.represented_control_count
      && metrics.rejected_control_count === 0
      && metrics.unresolved_control_count === 0
      && metrics.control_rejection_codes?.length === 0
      && metrics.propulsor_occurrence_count === metrics.represented_propulsor_count
      && metrics.rejected_propulsor_count === 0
      && metrics.unresolved_propulsor_count === 0
      && metrics.propulsor_rejection_codes?.length === 0
      && metrics.rejected_feature_ids.length === 0
      && metrics.unsupported_declarations.length === 0
      && metrics.semantic_rejections.length === 0;
  }
  return metrics.component_count !== null
    && metrics.supported_component_count === metrics.component_count
    && metrics.skipped_component_count === 0
    && metrics.rejected_feature_ids.length === 0
    && metrics.unsupported_declarations.length === 0
    && metrics.semantic_rejections.length === 0;
}

function completeRealization(metrics: HeartbeatLaneMetrics): boolean {
  return metrics.geometry_complete === true && metrics.skipped_surface_count === 0
    && metrics.source_component_count !== null
    && metrics.source_component_count === metrics.realized_component_count
    && metrics.source_surface_count !== null
    && metrics.source_surface_count === metrics.realized_surface_count;
}

function parseModel(value: unknown, path: string): HeartbeatModelResult {
  const source = row(value, path);
  exact(source, ["schema", "ordinal", "entry_id", "archive_member_path", "source_sha256", "started_at", "finished_at",
    "duration_ms", "lanes", "targets", "cluster_ids"], path);
  if (source.schema !== "loom.airshow-heartbeat-model/v1") throw new Error(`${path} schema is unsupported`);
  const identity = selectedModel({ entry_id: source.entry_id, archive_member_path: source.archive_member_path }, path);
  if (!Array.isArray(source.lanes) || source.lanes.length !== HEARTBEAT_LANES.length) throw new Error(`${path} requires all six lanes`);
  const lanes = source.lanes.map((child, index): HeartbeatLaneResult => {
    const itemPath = `${path}.lanes[${index}]`; const item = row(child, itemPath);
    exact(item, ["lane", "status", "code", "message", "duration_ms", "metrics"], itemPath);
    const result = {
      lane: oneOf(item.lane, HEARTBEAT_LANES, `${itemPath}.lane`),
      status: oneOf(item.status, ["passed", "failed", "unavailable", "skipped"] as const, `${itemPath}.status`),
      code: code(item.code, `${itemPath}.code`), message: text(item.message, `${itemPath}.message`),
      duration_ms: integer(item.duration_ms, `${itemPath}.duration_ms`, 86_400_000),
      metrics: parseMetrics(item.metrics, `${itemPath}.metrics`),
    };
    if (result.lane !== HEARTBEAT_LANES[index]) throw new Error(`${path} lane order is not canonical`);
    if (result.lane === "semantic" && result.status === "passed" && !completeSemantic(result.metrics)) throw new Error(`${path} semantic lane is incomplete`);
    if (result.lane === "display" && result.status === "passed" && !completeRealization(result.metrics)) throw new Error(`${path} display lane is incomplete`);
    if (result.lane === "exact" && result.status === "passed" && !(result.metrics.representation === "exact-bezier"
        && result.metrics.fidelity === "exact-attested" && result.metrics.export_order_attested === true
        && completeRealization(result.metrics))) throw new Error(`${path} exact lane lacks complete attestation`);
    return result;
  });
  const targets = canonicalTargets((source.targets as unknown[] | undefined)?.map((child) => row(child, `${path}.targets`).target), `${path}.targets`);
  const targetResults = (source.targets as unknown[]).map((child, index): HeartbeatTargetResult => {
    const itemPath = `${path}.targets[${index}]`; const item = row(child, itemPath);
    exact(item, ["target", "status", "code", "message"], itemPath);
    return { target: targets[index]!, status: oneOf(item.status, ["passed", "failed", "not-run"] as const, `${itemPath}.status`), code: code(item.code, `${itemPath}.code`), message: text(item.message, `${itemPath}.message`) };
  });
  const byLane = Object.fromEntries(lanes.map((item) => [item.lane, item]));
  const targetLanes: Record<HeartbeatTarget, readonly HeartbeatLane[]> = {
    typed: ["archive-source", "source-audit"], "semantic-complete": ["semantic"],
    renderable: ["display"], "exact-attested": ["exact"],
  };
  for (const target of targetResults) if (target.status === "passed" && targetLanes[target.target].some((name) => byLane[name]!.status !== "passed")) throw new Error(`${path} target pass lacks lane evidence`);
  if (byLane["archive-source"]!.status === "passed" && source.source_sha256 === null) throw new Error(`${path} archive source pass lacks its digest`);
  const started = timestamp(source.started_at, `${path}.started_at`);
  const finished = timestamp(source.finished_at, `${path}.finished_at`);
  if (Date.parse(finished) < Date.parse(started)) throw new Error(`${path} timestamps are reversed`);
  return {
    schema: "loom.airshow-heartbeat-model/v1", ordinal: integer(source.ordinal, `${path}.ordinal`), ...identity,
    source_sha256: nullable(source.source_sha256, (child) => sha(child, `${path}.source_sha256`)),
    started_at: started, finished_at: finished, duration_ms: integer(source.duration_ms, `${path}.duration_ms`, 86_400_000),
    lanes, targets: targetResults, cluster_ids: sortedUnique(source.cluster_ids, (child, childPath) => sha(child, childPath), `${path}.cluster_ids`),
  };
}

function parseCluster(value: unknown, path: string): HeartbeatFailureCluster {
  const source = row(value, path);
  exact(source, ["cluster_id", "lane", "code", "feature_declarations", "model_entry_ids", "count", "remedy"], path);
  const remedy = row(source.remedy, `${path}.remedy`); exact(remedy, ["kind", "summary", "automatic"], `${path}.remedy`);
  if (remedy.automatic !== false) throw new Error(`${path} remedy must remain manual; automatic remediation is forbidden`);
  const models = sortedUnique(source.model_entry_ids, entryId, `${path}.model_entry_ids`);
  const count = integer(source.count, `${path}.count`);
  if (count === 0 || count !== models.length) throw new Error(`${path} count disagrees with models`);
  return {
    cluster_id: sha(source.cluster_id, `${path}.cluster_id`), lane: oneOf(source.lane, HEARTBEAT_LANES, `${path}.lane`),
    code: code(source.code, `${path}.code`),
    feature_declarations: sortedUnique(source.feature_declarations, text, `${path}.feature_declarations`),
    model_entry_ids: models, count,
    remedy: {
      kind: oneOf(remedy.kind, ["archive-integrity", "source-parser", "semantic-primitive", "exact-control", "display-export", "historical-reference"] as const, `${path}.remedy.kind`),
      summary: text(remedy.summary, `${path}.remedy.summary`), automatic: false,
    },
  };
}

export function parseAirshowHeartbeatReport(value: unknown, expected?: { projectId?: string; runId?: string }): AirshowHeartbeatReport {
  const source = row(value, "heartbeat.report");
  exact(source, ["schema", "report_revision", "generated_at", "run", "selected_models", "summary", "models", "clusters"], "heartbeat.report");
  if (source.schema !== "loom.airshow-heartbeat-report/v1") throw new Error("heartbeat.report schema is unsupported");
  const reportRevision = sha(source.report_revision, "heartbeat.report.report_revision", true);
  const run = parseAirshowHeartbeatRun(source.run, expected);
  if (!isHeartbeatTerminal(run.state) || run.report_revision !== reportRevision) throw new Error("heartbeat report is not bound to its terminal run revision");
  if (!Array.isArray(source.selected_models) || !Array.isArray(source.models) || !Array.isArray(source.clusters)) throw new Error("heartbeat report arrays are unavailable");
  const selected = source.selected_models.map((item, index) => selectedModel(item, `heartbeat.report.selected_models[${index}]`));
  const models = source.models.map((item, index) => parseModel(item, `heartbeat.report.models[${index}]`));
  const clusters = source.clusters.map((item, index) => parseCluster(item, `heartbeat.report.clusters[${index}]`));
  const summary = row(source.summary, "heartbeat.report.summary");
  exact(summary, ["processed_models", "succeeded_models", "failed_models", "unprocessed_models", "cluster_count", "targets"], "heartbeat.report.summary");
  if (!Array.isArray(summary.targets)) throw new Error("heartbeat target summary is unavailable");
  const targetNames = canonicalTargets(summary.targets.map((item) => row(item, "heartbeat.report.summary.targets").target), "heartbeat.report.summary.targets");
  const targetSummary = summary.targets.map((child, index): HeartbeatTargetSummary => {
    const itemPath = `heartbeat.report.summary.targets[${index}]`; const item = row(child, itemPath);
    exact(item, ["target", "passed", "failed", "not_processed"], itemPath);
    return { target: targetNames[index]!, passed: integer(item.passed, `${itemPath}.passed`), failed: integer(item.failed, `${itemPath}.failed`), not_processed: integer(item.not_processed, `${itemPath}.not_processed`) };
  });
  const resultSummary = {
    processed_models: integer(summary.processed_models, "heartbeat.report.summary.processed_models"),
    succeeded_models: integer(summary.succeeded_models, "heartbeat.report.summary.succeeded_models"),
    failed_models: integer(summary.failed_models, "heartbeat.report.summary.failed_models"),
    unprocessed_models: integer(summary.unprocessed_models, "heartbeat.report.summary.unprocessed_models"),
    cluster_count: integer(summary.cluster_count, "heartbeat.report.summary.cluster_count"), targets: targetSummary,
  };
  if (selected.length !== run.total_models || models.length !== resultSummary.processed_models || clusters.length !== resultSummary.cluster_count
      || resultSummary.processed_models !== run.processed_models || resultSummary.succeeded_models !== run.succeeded_models
      || resultSummary.failed_models !== run.failed_models || resultSummary.unprocessed_models !== run.total_models - run.processed_models
      || models.some((item, index) => item.ordinal !== index || item.entry_id !== selected[index]?.entry_id
        || item.archive_member_path !== selected[index]?.archive_member_path)) throw new Error("heartbeat report summary or selected plan disagrees with run outcomes");
  if (new Set(selected.map((item) => item.entry_id)).size !== selected.length
      || models.some((item) => item.targets.map(({ target }) => target).join("\u001f") !== run.targets.join("\u001f"))) {
    throw new Error("heartbeat report selection or model targets disagree with the run");
  }
  for (const target of targetSummary) {
    const outcomes = models.map((item) => item.targets.find((candidate) => candidate.target === target.target)?.status);
    const expectedCounts = [outcomes.filter((item) => item === "passed").length, outcomes.filter((item) => item === "failed").length,
      run.total_models - models.length + outcomes.filter((item) => item === "not-run").length];
    if ([target.passed, target.failed, target.not_processed].some((count, index) => count !== expectedCounts[index])) throw new Error("heartbeat target outcome summary drifts from model evidence");
  }
  return { schema: "loom.airshow-heartbeat-report/v1", report_revision: reportRevision,
    generated_at: timestamp(source.generated_at, "heartbeat.report.generated_at"), run, selected_models: selected,
    summary: resultSummary, models, clusters };
}

export function parseAirshowHeartbeatModelsPage(value: unknown, expected: { projectId: string; runId: string }): AirshowHeartbeatModelsPage {
  const source = row(value, "heartbeat.models");
  exact(source, ["schema", "run_id", "project_id", "cursor", "limit", "next_cursor", "total", "items"], "heartbeat.models");
  if (source.schema !== "loom.airshow-heartbeat-model-page/v1" || !Array.isArray(source.items)) throw new Error("heartbeat model page schema is unsupported");
  const runId = uuid(source.run_id, "heartbeat.models.run_id"); const projectId = uuid(source.project_id, "heartbeat.models.project_id");
  if (runId !== expected.runId || projectId !== expected.projectId) throw new Error("heartbeat model page identity is stale");
  const cursor = integer(source.cursor, "heartbeat.models.cursor"); const limit = integer(source.limit, "heartbeat.models.limit", 100);
  if (limit < 1 || source.items.length > limit) throw new Error("heartbeat model page exceeds its limit");
  const total = integer(source.total, "heartbeat.models.total");
  const next = nullable(source.next_cursor, (child) => integer(child, "heartbeat.models.next_cursor"));
  const items = source.items.map((item, index) => parseModel(item, `heartbeat.models.items[${index}]`));
  if (cursor + items.length > total || items.some((item, index) => item.ordinal !== cursor + index)
      || (next !== null && (next !== cursor + items.length || next >= total))) throw new Error("heartbeat model page cursor is incoherent");
  return { schema: "loom.airshow-heartbeat-model-page/v1", run_id: runId, project_id: projectId, cursor, limit, next_cursor: next, total, items };
}
